import React from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/solid';

const ValidationChecklist = ({ checklist = [], errors = [], warnings = [] }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'ok':
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'error':
      case 'invalid':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'pending':
      default:
        return <ClockIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ok':
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'error':
      case 'invalid':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'pending':
      default:
        return 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700';
    }
  };

  // Contar status
  const counts = checklist.reduce(
    (acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    },
    { ok: 0, error: 0, warning: 0, pending: 0 }
  );

  const total = checklist.length;
  const progress = total > 0 ? (counts.ok / total) * 100 : 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Validação do Contrato
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {counts.ok}/{total} itens validados
        </div>
      </div>

      {/* Barra de Progresso */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600 dark:text-gray-400">Progresso</span>
          <span className="font-semibold text-gray-900 dark:text-white">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              progress === 100
                ? 'bg-green-500'
                : progress > 50
                ? 'bg-blue-500'
                : 'bg-yellow-500'
            }`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Resumo de Status */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {counts.ok || 0}
          </div>
          <div className="text-xs text-green-700 dark:text-green-500">Válidos</div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {counts.error || 0}
          </div>
          <div className="text-xs text-red-700 dark:text-red-500">Erros</div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {counts.warning || 0}
          </div>
          <div className="text-xs text-yellow-700 dark:text-yellow-500">Avisos</div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
            {counts.pending || 0}
          </div>
          <div className="text-xs text-gray-700 dark:text-gray-500">Pendentes</div>
        </div>
      </div>

      {/* Lista de Checklist */}
      <div className="space-y-2">
        {checklist.map((item, index) => (
          <div
            key={index}
            className={`
              flex items-start gap-3 p-3 rounded-lg border
              ${getStatusColor(item.status)}
              transition-all duration-200
            `}
          >
            <div className="flex-shrink-0 mt-0.5">{getStatusIcon(item.status)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.item}
                </p>
                {item.required && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded">
                    Obrigatório
                  </span>
                )}
              </div>
              {item.message && (
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {item.message}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Erros */}
      {errors && errors.length > 0 && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2">
            <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-2">
                Erros que impedem o envio:
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm text-red-700 dark:text-red-300">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Avisos */}
      {warnings && warnings.length > 0 && (
        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                Avisos (não impedem o envio):
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index} className="text-sm text-yellow-700 dark:text-yellow-300">
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Status de Aprovação */}
      {progress === 100 && errors.length === 0 && (
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-6 h-6 text-green-500" />
            <div>
              <h4 className="text-sm font-semibold text-green-900 dark:text-green-200">
                Contrato pronto para envio!
              </h4>
              <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                Todos os campos obrigatórios foram validados com sucesso.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationChecklist;